import type { DeliveryRecord } from '../types/delivery';
import { formatCurrency } from './currencyUtils';
import { calculateInternalAging, calculateCustomerAging } from './delivery';

const formatCurrencyForPdf = (amount: number | string): string => {
  const baseFormatted = formatCurrency(amount);
  const numericPortion = baseFormatted.replace(/[^0-9,.-]/g, '').trim();
  return `PHP ${numericPortion || '0.00'}`;
};

export interface ExportPdfOptions {
  data: DeliveryRecord[];
  reportTitle: string;
  companyName?: string;
  orientation?: 'landscape' | 'portrait';
  format?: 'a4' | 'a1';
  columns?: Array<{
    header: string;
    dataKey: keyof DeliveryRecord | string;
    cellWidth?: number;
    formatter?: (value: any, record: DeliveryRecord) => string;
  }>;
  filename?: string;
  showToast?: (options: {
    title: string;
    description: string;
    status: 'success' | 'error' | 'warning';
    duration: number;
  }) => void;
}

// Utility functions for formatting (these should match your component's functions)
const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

const calculateGPR = (delivery: DeliveryRecord): number => {
  const contractPrice = typeof delivery.estContractPrice === 'string' 
    ? parseFloat(delivery.estContractPrice) 
    : delivery.estContractPrice || 0;
  
  const deliveryCost = typeof delivery.estDeliveryCost === 'string' 
    ? parseFloat(delivery.estDeliveryCost) 
    : delivery.estDeliveryCost || 0;
  
  // Avoid division by zero
  if (deliveryCost === 0) return 0;

  // Calculate GPR as percentage: (estContractPrice - estDeliveryCost) / estDeliveryCost * 100
  const gpr = ((contractPrice - deliveryCost) / contractPrice) * 100;
  return Number(gpr.toFixed(4)); // Return as number with 4 decimal precision
};

const formatGPR = (delivery: DeliveryRecord): string => {
  const gpr = calculateGPR(delivery);
  if (gpr === 0) return 'N/A';
  return `${gpr.toFixed(2)}%`; // Display with 2 decimal places and % symbol
};

const calculateGrossProfit = (delivery: DeliveryRecord): string => {
  const revenue = typeof delivery.estContractPrice === 'string' 
    ? parseFloat(delivery.estContractPrice) 
    : delivery.estContractPrice || 0;

  const cost = typeof delivery.estDeliveryCost === 'string' 
    ? parseFloat(delivery.estDeliveryCost) 
    : delivery.estDeliveryCost || 0;

  return formatCurrencyForPdf(revenue - cost);
};

// Default column configurations
export const DEFAULT_SUMMARY_COLUMNS = [
  { header: 'WayBill#', dataKey: 'wayBillNumber', cellWidth: 25 },
  { header: 'TLO/DR', dataKey: 'tlo', cellWidth: 20 },
  { header: 'Route', dataKey: 'route', cellWidth: 30 },
  { header: 'Plate#', dataKey: 'plateNumber', cellWidth: 20 },
  { header: 'Driver', dataKey: 'driverName', cellWidth: 25 },
  { header: 'Driver Mobile', dataKey: 'driverMobile', cellWidth: 25 },
  { 
    header: 'Dispatched', 
    dataKey: 'dispatchDateTime', 
    cellWidth: 30,
    formatter: (value: any) => formatDateTime(value)
  },
  { header: 'Status', dataKey: 'statusDetails', cellWidth: 25 },
  { 
    header: 'Completed', 
    dataKey: 'completed_at', 
    cellWidth: 30,
    formatter: (value: any) => formatDateTime(value)
  },
  { 
    header: 'Internal Aging (days)', 
    dataKey: 'internal_aging_days', 
    cellWidth: 25,
    formatter: (_value: any, record: DeliveryRecord) => calculateInternalAging(record).toString()
  },
  { 
    header: 'Customer Aging (days)', 
    dataKey: 'customer_aging_days', 
    cellWidth: 25,
    formatter: (_value: any, record: DeliveryRecord) => calculateCustomerAging(record).toString()
  },
  { 
    header: 'Billing Status', 
    dataKey: 'billingStatus', 
    // cellWidth: 25 
  },
  { 
    header: 'Est Contract Price', 
    dataKey: 'estContractPrice', 
    cellWidth: 20,
    formatter: (value: any) => formatCurrencyForPdf(value).toString()
  },
  { 
    header: 'Est Delivery Cost', 
    dataKey: 'estDeliveryCost', 
    cellWidth: 20,
    formatter: (value: any) => formatCurrencyForPdf(value).toString()
  },
  { 
    header: 'GPR', 
    dataKey: 'gpr', 
    cellWidth: 15,
    formatter: (_value: any, record: DeliveryRecord) => formatGPR(record)
  },
  { 
    header: 'Gross Profit', 
    dataKey: 'grossProfit', 
    // cellWidth: 20,
    formatter: (_value: any, record: DeliveryRecord) => calculateGrossProfit(record).toString()
  },
  { header: 'Updated By', dataKey: 'updated_by', cellWidth: 25 },
];

export const DEFAULT_DETAILED_COLUMNS = [
  { header: 'TLO/DR', dataKey: 'tlo', cellWidth: 35 },
  { header: 'WayBill#', dataKey: 'wayBillNumber' },
  { header: 'Route', dataKey: 'route' },
  { header: 'Plate#', dataKey: 'plateNumber', cellWidth: 25 },
  { header: 'Driver', dataKey: 'driverName', cellWidth: 45 },
  { header: 'Driver Mobile', dataKey: 'driverMobile' },
  { header: 'Cargo Weight', dataKey: 'cargoWeight', cellWidth: 30 },
  { header: 'Approved Deadweight', dataKey: 'approvedDeadWeight', cellWidth: 25 },
  { 
    header: 'Dispatched', 
    dataKey: 'dispatchDateTime', 
    cellWidth: 25,
    formatter: (value: any) => formatDateTime(value)
  },
  { 
    header: 'Arrival Loading Area', 
    dataKey: 'arrivalLoadingArea', 
    cellWidth: 30,
    formatter: (value: any) => formatDateTime(value)
  },
  { 
    header: 'Arrival Unloading Area', 
    dataKey: 'arrivalUnloadingArea', 
    cellWidth: 30,
    formatter: (value: any) => formatDateTime(value)
  },
  { header: 'Delivery Status', dataKey: 'statusDetails' },
  { 
    header: 'Delivery Completed', 
    dataKey: 'completed_at', 
    cellWidth: 30,
    formatter: (value: any) => formatDateTime(value)
  },
  { header: 'Demurrage Counting', dataKey: 'demurrageCounting', cellWidth: 20 },
  { 
    header: 'Internal Aging', 
    dataKey: 'internal_aging', 
    cellWidth: 20,
    formatter: (_value: any, record: DeliveryRecord) => `${calculateInternalAging(record)} days`
  },
  { 
    header: 'Customer Aging', 
    dataKey: 'customer_aging', 
    formatter: (_value: any, record: DeliveryRecord) => `${calculateCustomerAging(record)} days`
  },
  { header: 'Billing Status', 
    dataKey: 'billingStatus', 
    // cellWidth: 25 
  },
  { 
    header: 'Est Contract Price', 
    dataKey: 'estContractPrice',
    formatter: (value: any) => formatCurrencyForPdf(value).toString()
  },
  { 
    header: 'Est Delivery Cost', 
    dataKey: 'estDeliveryCost',
    formatter: (value: any) => formatCurrencyForPdf(value).toString()
  },
  { 
    header: 'GPR', 
    dataKey: 'gpr',
    cellWidth: 20,
    formatter: (_value: any, record: DeliveryRecord) => formatGPR(record)
  },
  { 
    header: 'Gross Profit', 
    dataKey: 'grossProfit', 
    // cellWidth: 25,
    formatter: (_value: any, record: DeliveryRecord) => calculateGrossProfit(record).toString()
  },
  { 
    header: 'Updated At', 
    dataKey: 'updated_at', 
    cellWidth: 20,
    formatter: (value: any) => formatDateTime(value)
  },
  { header: 'Updated By', dataKey: 'updated_by' },
];

/**
 * Unified PDF export function for delivery records
 */
export const exportDeliveryToPDF = async (options: ExportPdfOptions): Promise<void> => {
  const {
    data,
    reportTitle,
    companyName,
    orientation = 'landscape',
    format = 'a4',
    columns = DEFAULT_SUMMARY_COLUMNS,
    filename,
    showToast
  } = options;

  try {
    // Validate data
    if (!data || data.length === 0) {
      showToast?.({
        title: 'No Data',
        description: 'No data available to export',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Dynamically import jsPDF and autoTable
    const { default: jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;

    // Create PDF
    const doc = new jsPDF({ orientation, unit: 'mm', format });
    
    // Add title
    doc.setFontSize(16);
    doc.text(reportTitle, 20, 20);
    
    // Add company info if available
    if (companyName) {
      doc.setFontSize(12);
      doc.text(`Company: ${companyName}`, 20, 30);
    }
    
    // Add export date and record count
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, companyName ? 40 : 30);
    doc.text(`Total Records: ${data.length}`, 20, companyName ? 48 : 38);

    // Process table data
    const rows: string[][] = data.map(record => 
      columns.map(col => {
        const value = (record as any)[col.dataKey];
        if (col.formatter) {
          return col.formatter(value, record);
        }
        return String(value || '');
      })
    );

    // Generate column styles
    const columnStyles: Record<number, { cellWidth?: number }> = {};
    columns.forEach((col, index) => {
      if (col.cellWidth) {
        columnStyles[index] = { cellWidth: col.cellWidth };
      }
    });

    // Generate table
    autoTable(doc, {
      startY: companyName ? 55 : 45,
      head: [columns.map(col => col.header)],
      body: rows,
      styles: { 
        fontSize: format === 'a1' ? 10 : 8, 
        overflow: 'linebreak' 
      },
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: format === 'a1' ? 10 : 20, right: format === 'a1' ? 10 : 20 },
      theme: 'grid',
      columnStyles: columnStyles,
      tableWidth: format === 'a1' ? 'auto' : 'wrap',
    });

    // Calculate totals for last row
    const totalGrossProfit = formatCurrencyForPdf(data.reduce((sum, record) => {
      const revenue = typeof record.estContractPrice === 'string' ? parseFloat(record.estContractPrice) : record.estContractPrice || 0;
      const cost = typeof record.estDeliveryCost === 'string' ? parseFloat(record.estDeliveryCost) : record.estDeliveryCost || 0;
      return sum + (revenue - cost);
    }, 0));
    const totalContractPrice = formatCurrencyForPdf(data.reduce((sum, record) => {
      const revenue = typeof record.estContractPrice === 'string' ? parseFloat(record.estContractPrice) : record.estContractPrice || 0;
      return sum + revenue;
    }, 0));
    const totalCost = formatCurrencyForPdf(data.reduce((sum, record) => {
      const cost = typeof record.estDeliveryCost === 'string' ? parseFloat(record.estDeliveryCost) : record.estDeliveryCost || 0;
      return sum + cost;
    }, 0));

    // Build last row with totals
    const totalRow = columns.map(col => {
      if (col.dataKey === 'grossProfit') return totalGrossProfit;
      if (col.dataKey === 'estContractPrice') return totalContractPrice;
      if (col.dataKey === 'estDeliveryCost') return totalCost;
      return '';
    });
    // Add label to first column
    if (totalRow.length > 0) totalRow[0] = 'TOTAL';

    // Add last row to table
    rows.push(totalRow);

    // Regenerate table with totals
    autoTable(doc, {
      startY: companyName ? 55 : 45,
      head: [columns.map(col => col.header)],
      body: rows,
      styles: { 
        fontSize: format === 'a1' ? 10 : 8, 
        overflow: 'linebreak' 
      },
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: format === 'a1' ? 10 : 20, right: format === 'a1' ? 10 : 20 },
      theme: 'grid',
      columnStyles: columnStyles,
      tableWidth: format === 'a1' ? 'auto' : 'wrap',
    });

    // Generate filename if not provided
    const finalFilename = filename || generateFilename(reportTitle);

    // Save or share PDF
    const pdfBlob = doc.output('blob');
    const pdfFile = new File([pdfBlob], finalFilename, { type: 'application/pdf' });

    // Try to use native sharing if available
    if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
      await navigator.share({
        files: [pdfFile],
        title: reportTitle,
        text: 'Shared from Mobile Reporting App',
      });
    } else {
      // Fallback to download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast?.({
        title: 'PDF Downloaded',
        description: `${finalFilename} has been downloaded`,
        status: 'success',
        duration: 3000,
      });
    }
  } catch (error) {
    console.error('Error exporting PDF:', error);
    showToast?.({
      title: 'Export Failed',
      description: 'Failed to export PDF. Please try again.',
      status: 'error',
      duration: 5000,
    });
  }
};

/**
 * Generate filename based on report title and current date
 */
const generateFilename = (reportTitle: string): string => {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
  const titleSlug = reportTitle
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_');
  
  return `${titleSlug}_${dateStr}.pdf`;
};

/**
 * Quick export functions for common use cases
 */
export const exportSummaryPDF = (
  data: DeliveryRecord[], 
  reportTitle: string, 
  companyName?: string,
  showToast?: ExportPdfOptions['showToast']
) => {
  return exportDeliveryToPDF({
    data,
    reportTitle,
    companyName,
    orientation: 'landscape',
    format: 'a4',
    columns: DEFAULT_SUMMARY_COLUMNS,
    showToast
  });
};

export const exportDetailedPDF = (
  data: DeliveryRecord[], 
  reportTitle: string, 
  companyName?: string,
  showToast?: ExportPdfOptions['showToast']
) => {
  
  return exportDeliveryToPDF({
    data,
    reportTitle,
    companyName,
    orientation: 'landscape',
    format: 'a1',
    columns: DEFAULT_DETAILED_COLUMNS,
    showToast
  });
};
